/**
 * Named saved builds. A "build" is a snapshot of WizardState (sans the
 * `step`/`completed` UI bookkeeping) that the user has named and stored.
 *
 * Storage: idb-keyval under `saved-builds:v1` as an array. We keep the
 * shape flat and forward-compat (unknown fields preserved on write).
 */

import { get as idbGet, set as idbSet } from 'idb-keyval';

import { migrateState, type WizardState } from './wizard';

export interface SavedBuild {
  id: string;
  name: string;
  /** ISO timestamp of last save */
  savedAt: string;
  /** the wizard fields that define a build (no `step`/`completed`) */
  state: Omit<WizardState, 'step' | 'completed'>;
}

const KEY = 'saved-builds:v1';
const MAX_SLOTS = 20;

export async function listBuilds(): Promise<SavedBuild[]> {
  const raw = (await idbGet<SavedBuild[]>(KEY)) ?? [];
  // newest first, with on-the-fly migration so old saves keep loading.
  return raw
    .map((b) => ({
      ...b,
      state: migrateState(b.state as Partial<WizardState>) as SavedBuild['state'],
    }))
    .sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

export async function saveBuild(name: string, w: WizardState): Promise<SavedBuild> {
  const trimmed = name.trim().slice(0, 60) || `Build ${new Date().toISOString().slice(0, 10)}`;
  const id = crypto.randomUUID();
  const build: SavedBuild = {
    id,
    name: trimmed,
    savedAt: new Date().toISOString(),
    state: {
      characterId: w.characterId,
      characterClass: w.characterClass,
      exoticHash: w.exoticHash,
      exoticLocked: w.exoticLocked,
      setSelections: w.setSelections.map((s) => ({ ...s })),
      targetStats: [...w.targetStats] as WizardState['targetStats'],
      subclassHash: w.subclassHash,
      superHash: w.superHash,
      fragmentHashes: [...w.fragmentHashes],
      aspectHashes: [...w.aspectHashes],
      abilityHashes: { ...w.abilityHashes },
      classItemPerks: { ...w.classItemPerks },
      fragmentStats: [...w.fragmentStats] as WizardState['fragmentStats'],
      // Deep-clone solverOptions so a later wizard mutation doesn't
      // bleed into the saved build's snapshot via shared references.
      solverOptions: {
        ...w.solverOptions,
        artifice: { ...w.solverOptions.artifice },
      },
    },
  };
  const existing = (await idbGet<SavedBuild[]>(KEY)) ?? [];
  const next = [build, ...existing].slice(0, MAX_SLOTS);
  await idbSet(KEY, next);
  return build;
}

export async function deleteBuild(id: string): Promise<void> {
  const existing = (await idbGet<SavedBuild[]>(KEY)) ?? [];
  await idbSet(
    KEY,
    existing.filter((b) => b.id !== id),
  );
}

export async function renameBuild(id: string, name: string): Promise<void> {
  const trimmed = name.trim().slice(0, 60);
  if (!trimmed) return;
  const existing = (await idbGet<SavedBuild[]>(KEY)) ?? [];
  await idbSet(
    KEY,
    existing.map((b) => (b.id === id ? { ...b, name: trimmed } : b)),
  );
}

/** Pull a build's state into a WizardState patch (caller decides which step
 *  to land on). */
export function patchFromBuild(b: SavedBuild): Partial<WizardState> {
  return { ...b.state };
}

/** Export shape — versioned so future format changes can migrate safely. */
export interface BuildsExport {
  format: 'cryptarch-builds';
  version: 1;
  exportedAt: string;
  builds: SavedBuild[];
}

export async function exportBuilds(): Promise<BuildsExport> {
  const builds = await listBuilds();
  return {
    format: 'cryptarch-builds',
    version: 1,
    exportedAt: new Date().toISOString(),
    builds,
  };
}

export interface ImportResult {
  added: number;
  skipped: number;
  total: number;
}

/** Import builds from a parsed JSON. De-dupes by id (skips builds with an id
 *  that already exists). Respects MAX_SLOTS — overflow is dropped after merge.
 *  Throws if the input isn't a valid BuildsExport. */
export async function importBuilds(data: unknown): Promise<ImportResult> {
  if (!isBuildsExport(data)) {
    throw new Error('not a cryptarch-builds export file');
  }
  const existing = (await idbGet<SavedBuild[]>(KEY)) ?? [];
  const seen = new Set(existing.map((b) => b.id));
  const incoming = data.builds.filter((b) => !seen.has(b.id));
  const merged = [...incoming, ...existing]
    .sort((a, b) => b.savedAt.localeCompare(a.savedAt))
    .slice(0, MAX_SLOTS);
  await idbSet(KEY, merged);
  return {
    added: incoming.length,
    skipped: data.builds.length - incoming.length,
    total: merged.length,
  };
}

function isBuildsExport(x: unknown): x is BuildsExport {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    o.format === 'cryptarch-builds' &&
    o.version === 1 &&
    Array.isArray(o.builds) &&
    (o.builds as unknown[]).every(
      (b) =>
        b !== null &&
        typeof b === 'object' &&
        typeof (b as { id?: unknown }).id === 'string' &&
        typeof (b as { name?: unknown }).name === 'string' &&
        typeof (b as { savedAt?: unknown }).savedAt === 'string' &&
        (b as { state?: unknown }).state !== null &&
        typeof (b as { state?: unknown }).state === 'object',
    )
  );
}
